import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, Tooltip, XAxis, TooltipProps } from 'recharts';

import DefaultApiLimit from '../../../../config/defaultLimit';
import DefaultApiParameters from '../../../../enum/DefaultApiParameters';
import currentTimeZone from '../../../../enum/timeZone';
import { ToggleListProperties } from '../../../../interface/device-management.interface';
import {
  GuestChargingProperties,
  RequestGuestChargingInterface,
} from '../../../../interface/guest-charging-schedule.interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  fetchGuestCharging,
  fetchListGuestChargingFilters,
  updateGuestChargingFilters,
  setSortData,
} from '../../../../redux/slices/GuestChargingSlice';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import DisableSwitchWarning from '../../../../shared-components/disable-warning/DisableWarning';
import DurationToggle from '../../../../shared-components/duration-toggle/DurationToggle';
import ModalBox from '../../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../../shared-components/success-notification/SuccessNotification';
import SwitchButton from '../../../../shared-components/switch-button-field/SwitchButton';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../../shared-functions/ConvertUTCtoDate';
import deviceApi from '../../../../utils/api/device';

interface ChartDataProperties {
  date: string;
  label?: string;
  freeTransactionCount: number;
  month?: string;
  paidTransactionCount: number;
  totalCount?: number;
  year?: number;
  hour: number;
}

interface ModelBoxDetail {
  title: String | null;
  header: boolean;
  content: JSX.Element | null;
  notificationModel: boolean;
}

/**
 * Function to fill missing months in chart
 * @param data
 * @returns
 */
function fillMissingMonths(data: ChartDataProperties[]) {
  const today = new Date();
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  // Generate past 12 months dynamically
  const past12Months = [];
  for (let index = 0; index < 12; index += 1) {
    const date = new Date(today.getFullYear(), today.getMonth() - index, 1); // Get the first day of the month
    past12Months.push({
      label: months[date.getMonth()], // Month name
      year: date.getFullYear(), // Year
    });
  }

  // Map input data for easy lookup
  const freeTransactionCountMap = new Map(
    data.map((item) => [item.month, item.freeTransactionCount]),
  );
  const paidTransactionCountMap = new Map(
    data.map((item) => [item.month, item.paidTransactionCount]),
  );

  // Generate result array with missing months filled
  const result = past12Months.map(({ label, year }) => {
    const key = label; // Use the month name as key
    return {
      hour: 0,
      date: '',
      label: `${label} ${year}`, // Combine month and year for output
      freeTransactionCount: freeTransactionCountMap.get(key) || 0, // Default to 0 if month is missing
      paidTransactionCount: paidTransactionCountMap.get(key) || 0, // Default 0
    };
  });

  return result.reverse();
}

/**
 * Function to generate the map data for past 30 days
 * @param data
 * @returns
 */
function fillMissingLastMonth(data: ChartDataProperties[]) {
  const today = new Date();
  const past30Days = [];

  // Generate the past 30 days as ISO date strings
  for (let index = 0; index < 30; index += 1) {
    const date = new Date(today);
    date.setDate(today.getDate() - index);
    const isoDate = date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
    past30Days.push({ date, isoDate });
  }

  // Map input data using full ISO date for lookup
  const freeTransactionCountMap = new Map(
    data.map((item) => [item.date.split('T')[0], item.freeTransactionCount]),
  );
  const paidTransactionCountMap = new Map(
    data.map((item) => [item.date.split('T')[0], item.paidTransactionCount]),
  );

  // Date formatter to get "Mar 18" format
  const dateFormatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
  });

  // Build the result array
  const result = past30Days.map(({ date, isoDate }) => ({
    hour: 0,
    date: dateFormatter.format(date),
    label: dateFormatter.format(date), // Format label as "Mar 18"
    freeTransactionCount: freeTransactionCountMap.get(isoDate) || 0, // Default to 0
    paidTransactionCount: paidTransactionCountMap.get(isoDate) || 0, // Default to 0
  }));

  return result.reverse();
}

/**
 * Function to add missing data in last 7 days
 * @param data
 * @returns
 */
function fillMissingLast7Days(data: ChartDataProperties[]) {
  const today = new Date();
  const last7Days = [];

  // Generate the last 7 days as ISO date strings
  for (let index = 0; index < 7; index += 1) {
    const date = new Date(today);
    date.setDate(today.getDate() - index);
    last7Days.push(date);
  }

  // Map input data using full ISO date for lookup
  const freeTransactionCountMap = new Map(
    data.map((item) => [item.date.split('T')[0], item.freeTransactionCount]),
  );
  const paidTransactionCountMap = new Map(
    data.map((item) => [item.date.split('T')[0], item.paidTransactionCount]),
  );
  // Date formatter to get "Nov 14" format
  const dateFormatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
  });

  // Build the result array
  const result = last7Days.map((date) => {
    const isoDate = date.toISOString().split('T')[0]; // Use ISO date for lookup
    return {
      hour: 0,
      date: dateFormatter.format(date),
      label: dateFormatter.format(date), // Format label as "Mar 18"
      freeTransactionCount: freeTransactionCountMap.get(isoDate) || 0, // Default to 0
      paidTransactionCount: paidTransactionCountMap.get(isoDate) || 0, // Default to 0
    };
  });

  return result.reverse();
}

/**
 * Function to handle missing data for the last 24 hours based on the current time zone
 * @param {ChartDataProperties[]} data - The input data
 * @param {string} timeZone - The time zone (e.g., "Asia/Kolkata", "America/New_York")
 * @returns {object[]} The result with complete data for the last 24 hours
 */
function fillMissingLast24HrsData(data: ChartDataProperties[]) {
  const now = new Date();
  const past24Hours = [];

  for (let index = 0; index < 24; index += 1) {
    const date = new Date(now);
    date.setHours(now.getHours() - index, 0, 0, 0); // Adjust hour, reset minutes/seconds/ms

    // Format date and hour based on the specified time zone
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: currentTimeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      hourCycle: 'h23', // Ensures 0-23 hour format
    });

    const formattedDate = formatter.formatToParts(date);
    const year = formattedDate.find((p) => p.type === 'year')?.value;
    const month = formattedDate.find((p) => p.type === 'month')?.value;
    const day = formattedDate.find((p) => p.type === 'day')?.value;
    const hour = formattedDate.find((p) => p.type === 'hour')?.value;

    // Ensure YYYY-MM-DD format
    const isoDate = `${year}-${month}-${day}`;

    past24Hours.push({ date: isoDate, hour: Number(hour) });
  }

  // Build the result array
  const result = past24Hours.map((eachHour) => {
    const dataForHour = data.find(
      (eachData) => eachData.date === eachHour.date && eachData.hour === eachHour.hour,
    );

    return {
      hour: 0,
      date: '',
      label: eachHour.hour.toString(), // Hour (0-23)
      freeTransactionCount: dataForHour ? dataForHour.freeTransactionCount : 0, // Default to 0 if data not found
      paidTransactionCount: dataForHour ? dataForHour.paidTransactionCount : 0, // Default to 0 if data not found
    };
  });

  return result.reverse(); // Ensuring chronological order
}

/**
 * Guest charging component
 * @returns
 */
function GuestCharging() {
  const { t } = useTranslation('devices');
  const dispatch = useAppDispatch();
  const { selectedChargingPoint } = useAppSelector((s) => s.device);
  const {
    schedules,
    page,
    limit,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    sortField,
    sortBy,
  } = useAppSelector((s) => s.guestCharging);
  const lineChartContainerReference = useRef<HTMLDivElement>(null);
  const [lineChartSize, setLineChartSize] = useState({
    width: 0,
    height: 0,
  });

  const [toggleList, setToggleList] = useState<ToggleListProperties[]>([
    {
      label: t('last24Hours'),
      key: 'last24hours',
      isActive: true,
    },
    {
      label: t('last7Days'),
      key: 'last7days',
      isActive: false,
    },
    {
      label: t('lastMonth'),
      key: 'lastMonth',
      isActive: false,
    },
    {
      label: t('last12Months'),
      key: 'last12months',
      isActive: false,
    },
  ]);

  const [chartData, setChartData] = useState<ChartDataProperties[]>([]);

  const [chartLabelData, setChartLabelData] = useState([
    {
      label: t('paid'),
      key: 'paid',
      value: 30,
      color: '#6EC282',
    },
    {
      label: t('guest'),
      key: 'guest',
      value: 50,
      color: '#CF5151',
    },
  ]);
  const [modelBoxStatus, setModelBoxStatus] = useState<'open' | 'close'>('close');
  const [modelBoxDetails, setModelBoxDetails] = useState<ModelBoxDetail>({
    title: '',
    header: false,
    content: null,
    notificationModel: false,
  });

  /**
   * Renders the time
   * @param device
   * @returns
   */
  const renderTime = (device: GuestChargingProperties) => (
    <div className="charging-time-list">
      <div className="first-time">{device.time[0]}</div>
      <div className="count">{device.time.length - 1}</div>
    </div>
  );

  /**
   * Handle the fetch data for Api call function
   * @param data
   */
  const fetchData = (data: RequestGuestChargingInterface) => {
    dispatch(
      fetchGuestCharging({
        ...data,
        chargingPointSn: selectedChargingPoint?.chargingPointSn || '',
      }),
    );
  };

  /**
   * Function for updating availability
   * @param deviceId
   * @param isAvailable
   */
  const updateAvailability = (deviceId: string, checked: boolean) => {
    deviceApi
      .editDeviceStatus(deviceId, { isActive: checked })
      .then(() => {
        setModelBoxStatus('open');
        setModelBoxDetails({
          title: '',
          header: false,
          content: (
            <SuccessNotification
              title={t('statusChangedSuccessfully')}
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={() => {
                setModelBoxStatus('close');
                fetchData({ page: DefaultApiLimit.page, limit });
              }}
            />
          ),
          notificationModel: true,
        });
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Handling the availability changes
   * @param device
   * @param checked
   */
  const handleAvailabilityChange = (device: GuestChargingProperties, checked: boolean) => {
    setModelBoxStatus('open');
    setModelBoxDetails({
      title: checked ? t('enableDeviceWarning') : t('disableDeviceWarning'),
      content: (
        <DisableSwitchWarning
          selectedId={device._id}
          onCancel={() => setModelBoxStatus('close')}
          message={checked ? t('enableWarningMessage') : t('disableWarningMessage')}
          onConfirm={() => updateAvailability(device._id, checked)}
        />
      ),
      notificationModel: true,
      header: false,
    });
  };

  /**
   * Function for rendering availability
   * @param device
   * @returns
   */
  const renderStatus = (device: GuestChargingProperties) => (
    <div className="flex justify-center">
      <div key={device.id}>
        <SwitchButton
          checked={device.isActive}
          change={(checked: boolean) => handleAvailabilityChange(device, checked)}
        />
      </div>
    </div>
  );

  const column = [
    {
      label: t('consumerId'),
      accessorKey: 'accountCustomId' as keyof GuestChargingProperties,
      sortAllow: true,
    },
    {
      label: t('name'),
      accessorKey: 'name' as keyof GuestChargingProperties,
      sortAllow: true,
    },
    {
      label: t('phoneNumber'),
      accessorKey: 'phoneNumber' as keyof GuestChargingProperties,
      sortAllow: true,
    },
    {
      label: t('email'),
      accessorKey: 'email' as keyof GuestChargingProperties,
      sortAllow: true,
    },
    {
      label: t('assignedStation'),
      accessorKey: 'chargingStationNames' as keyof GuestChargingProperties,
      sortAllow: false,
    },
    {
      label: t('device'),
      accessorKey: 'chargingPointSerials' as keyof GuestChargingProperties,
      sortAllow: false,
    },
    {
      label: t('date'),
      accessorKey: 'date' as keyof GuestChargingProperties,
      sortAllow: false,
      /**
       * Date view
       * @param station
       * @param index
       */
      customData: (station: GuestChargingProperties) => (
        <div className="table-row-each-data" style={{ alignSelf: 'center' }}>
          {ConvertUTCtoDate(station.fromDate)} - {ConvertUTCtoDate(station.toDate)}
        </div>
      ),
    },
    {
      label: t('time'),
      accessorKey: 'time' as keyof GuestChargingProperties,
      sortAllow: false,
      columnWidth: 170,
      customData: renderTime,
    },
    {
      label: t('duration'),
      accessorKey: 'duration' as keyof GuestChargingProperties,
      sortAllow: false,
    },
    {
      label: t('status'),
      accessorKey: 'isAvailable' as keyof GuestChargingProperties,
      customData: renderStatus,
    },
  ];

  /**
   * Function to change the active menu
   * @param key
   */
  const handleClickFunction = (key: string) => {
    setToggleList((previousValue) =>
      previousValue.map((eachValue) => {
        let updatedValue = eachValue;
        if (eachValue.key === key) {
          updatedValue = {
            ...eachValue,
            isActive: true,
          };
        } else {
          updatedValue = {
            ...eachValue,
            isActive: false,
          };
        }
        return updatedValue;
      }),
    );
  };
  /**
   * Handle the filter data clear function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    dispatch(
      fetchGuestCharging({
        ...queryParameters,
        sortBy: sortData.sortBy,
        sortField: sortData.sortField,
        page: DefaultApiLimit.page,
        limit,
        chargingPointSn: selectedChargingPoint?.chargingPointSn || '',
      }),
    );
  };

  /**
   * Table pagination function
   * @param value
   */
  const pageNateLimitChange = (value: GuestChargingProperties) => {
    dispatch(
      fetchGuestCharging({
        ...value,
        sortField,
        sortBy,
        chargingPointSn: selectedChargingPoint?.chargingPointSn || '',
      }),
    );
  };

  /**
   * Function to fetch report for charging schedule
   */
  const handleFetchChargingScheduleReport = async (timePeriod: string) => {
    try {
      const response = await deviceApi.fetchChargingScheduleReport({
        chargingPointSn: selectedChargingPoint?.chargingPointSn,
        timeZone: currentTimeZone,
        timePeriod,
      });
      let finalData: ChartDataProperties[] = [];
      if (timePeriod === 'last12months') {
        finalData = fillMissingMonths(response.data);
      } else if (timePeriod === 'lastMonth') {
        finalData = fillMissingLastMonth(response.data);
      } else if (timePeriod === 'last7days') {
        finalData = fillMissingLast7Days(response.data);
      } else if (timePeriod === 'last24hours') {
        finalData = fillMissingLast24HrsData(response.data);
      }
      setChartData(finalData);
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * Function to show custom tooltip
   * @param param0
   * @returns
   */
  function CustomTooltip({ active, payload }: TooltipProps<number, string>) {
    if (active && payload && payload.length > 0) {
      const data = payload[0];
      return (
        <div className="charging-schedule-custom-tooltip">
          <p className="label">{data?.payload?.label}</p>
          <div className="tooltip-values">
            <p style={{ color: '#6EC282' }}>
              {t('paid')}: {data?.payload?.paidTransactionCount}
            </p>
            <p style={{ color: '#CF5151' }}>
              {t('guest')}: {data?.payload?.freeTransactionCount || 0}
            </p>
          </div>
        </div>
      );
    }

    return null;
  }

  useEffect(() => {
    const width = lineChartContainerReference.current?.offsetWidth || 0;
    const height = lineChartContainerReference.current?.offsetHeight || 0;
    setLineChartSize({
      width: width - 25,
      height,
    });
  }, [lineChartContainerReference.current]);

  useEffect(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    dispatch(
      fetchGuestCharging({
        page: DefaultApiParameters.page,
        limit: DefaultApiParameters.limit,
        chargingPointSn: selectedChargingPoint?.chargingPointSn || '',
        ...queryParameters,
      }),
    );
  }, [filters, selectedChargingPoint]);

  useEffect(() => {
    handleFetchChargingScheduleReport(
      toggleList.find((toggle) => toggle.isActive === true)?.key || 'last24hours',
    );
  }, [toggleList]);

  useEffect(() => {
    setChartLabelData([
      {
        label: t('paid'),
        key: 'paid',
        value: chartData.reduce((accumulator, each) => accumulator + each.paidTransactionCount, 0),
        color: '#6EC282',
      },
      {
        label: t('guest'),
        key: 'guest',
        value: chartData.reduce((accumulator, each) => accumulator + each.freeTransactionCount, 0),
        color: '#CF5151',
      },
    ]);
  }, [chartData]);

  return (
    <div className="device-guest-charging-wrap">
      <div className="device-guest-charging-table">
        <ModalBox
          open={modelBoxStatus === 'open'}
          title={modelBoxDetails.title}
          content={modelBoxDetails.content}
          closeModal={() => setModelBoxStatus('close')}
          header={modelBoxDetails.header}
          rightCorner=""
          // modalBoxAnimation='modalAnimateToLeft'
        />
        <CustomTable
          header={column}
          data={schedules}
          serialNo
          filter={{}}
          filterClearFunction={() => {}}
          filterFunction={() => {}}
          actionButton={false}
          limit={limit}
          page={page}
          loading={loading}
          totalPages={totalPages}
          totalResults={totalResults}
          sortData={{ sortBy, sortField }}
          handleSortChange={handleSortFunction}
          pageLimitChange={(value: any) => pageNateLimitChange(value)}
          sizeChangeButtonRequired
          filters={filters}
          filtersToDispatch={updateGuestChargingFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListGuestChargingFilters}
          version="v2"
        />
      </div>
      <div className="device-guest-charging-chart-wrap" ref={lineChartContainerReference}>
        <h6>
          {t('paid')} {t('vs')} {t('guest')}
        </h6>
        <div className="guest-charging-chart-container">
          <BarChart width={lineChartSize.width} height={lineChartSize.width / 2} data={chartData}>
            <XAxis
              dataKey="label"
              axisLine={false}
              tickLine={false}
              fontSize={10}
              color="#535353"
            />
            {/* <YAxis axisLine={false} tickLine={false} /> */}
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
            <Bar
              dataKey="freeTransactionCount"
              stackId="a"
              radius={4}
              barSize={26}
              fill="#F04438"
            />
            <Bar
              dataKey="paidTransactionCount"
              stackId="a"
              radius={4}
              barSize={26}
              fill="#2E9E3A"
            />
          </BarChart>
          <DurationToggle toggleList={toggleList} handleClickFunction={handleClickFunction} />
        </div>
        <div className="device-guest-charging-label-wrap">
          {chartLabelData.map((eachLabel) => (
            <div
              className="each-label-container"
              style={{ '--bgColor': eachLabel.color } as React.CSSProperties}>
              <span>{eachLabel.label}</span>
              <span>{eachLabel.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default GuestCharging;
